// frontend/app/components/KtPopup.js
// Knowledge Tracing — casual MCQ popup that slides up from the bottom-right
// to probe student mastery without interrupting the learning flow.

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Animated,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { KT_AUTO_DISMISS_MS, KT_FEEDBACK_MS } from "../constants/kt";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUTO_DISMISS_MS = KT_AUTO_DISMISS_MS;
const FEEDBACK_MS = KT_FEEDBACK_MS;
const SLIDE_DURATION = 320; // ms for slide animation
const MAX_WIDTH = 480;
const COUNTDOWN_INTERVAL = 1_000; // 1 s tick for countdown

const OPTION_LABELS = ["A", "B", "C", "D"];

const DIFFICULTY_BADGES = {
  basic: { label: "Basic", color: "#10B981", darkColor: "#34d399" },
  intermediate: { label: "Intermediate", color: "#3B82F6", darkColor: "#60a5fa" },
  advanced: { label: "Advanced", color: "#8B5CF6", darkColor: "#a78bfa" },
};

// ---------------------------------------------------------------------------
// Color helper (mirrors getMainContentColors pattern)
// ---------------------------------------------------------------------------

const getColors = (isDark) => ({
  cardBg: isDark ? "#1e293b" : "#ffffff",
  textPrimary: isDark ? "#f1f5f9" : "#0f172a",
  textSecondary: isDark ? "#94a3b8" : "#64748b",
  textMuted: isDark ? "#64748b" : "#94a3b8",
  border: isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(148, 163, 184, 0.2)",
  optionBg: isDark ? "#0f172a" : "#f8fafc",
  optionBgHover: isDark ? "rgba(129, 140, 248, 0.12)" : "rgba(99, 102, 241, 0.08)",
  optionBorder: isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(148, 163, 184, 0.18)",
  accentPrimary: isDark ? "#818cf8" : "#6366f1",
  correctBg: isDark ? "rgba(52, 211, 153, 0.15)" : "rgba(16, 185, 129, 0.1)",
  correctBorder: isDark ? "#34d399" : "#10B981",
  incorrectBg: isDark ? "rgba(248, 113, 113, 0.15)" : "rgba(239, 68, 68, 0.1)",
  incorrectBorder: isDark ? "#f87171" : "#ef4444",
  skipColor: isDark ? "#94a3b8" : "#64748b",
  backdrop: isDark ? "rgba(0, 0, 0, 0.45)" : "rgba(0, 0, 0, 0.25)",
  shadow: isDark ? "#000" : "#64748b",
  shadowOpacity: isDark ? 0.45 : 0.12,
  countdownTrack: isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(148, 163, 184, 0.18)",
  countdownFill: isDark ? "#818cf8" : "#6366f1",
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function KtPopup({
  question,
  visible,
  isDarkMode,
  onAnswer,
  onSkip,
  onDismiss,
  onDisablePopups,
}) {
  const { width: windowWidth } = useWindowDimensions();
  const colors = useMemo(() => getColors(isDarkMode), [isDarkMode]);

  // --- animation state --------------------------------------------------
  const slideAnim = useRef(new Animated.Value(0)).current; // 0 = hidden, 1 = shown
  const [renderPopup, setRenderPopup] = useState(false);

  // --- interaction state ------------------------------------------------
  const [selectedIdx, setSelectedIdx] = useState(null); // 0–3
  const [feedbackType, setFeedbackType] = useState(null); // 'correct' | 'incorrect' | null
  const [secondsLeft, setSecondsLeft] = useState(AUTO_DISMISS_MS / 1000);

  // refs for timers so we can clear on unmount / early dismiss
  const autoDismissTimer = useRef(null);
  const feedbackTimer = useRef(null);
  const countdownTimer = useRef(null);

  // --- derived ----------------------------------------------------------
  const options = useMemo(() => {
    if (!question?.options) return [];
    const { option1, option2, option3, option4 } = question.options;
    return [option1, option2, option3, option4].filter(Boolean);
  }, [question]);

  const diffBadge = DIFFICULTY_BADGES[question?.difficulty_tier] || DIFFICULTY_BADGES.basic;

  // --- helpers ----------------------------------------------------------
  const clearAllTimers = useCallback(() => {
    if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    autoDismissTimer.current = null;
    feedbackTimer.current = null;
    countdownTimer.current = null;
  }, []);

  const animateOut = useCallback(
    (cb) => {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: SLIDE_DURATION,
        useNativeDriver: true,
      }).start(() => {
        setRenderPopup(false);
        if (cb) cb();
      });
    },
    [slideAnim],
  );

  // --- show / hide lifecycle --------------------------------------------
  useEffect(() => {
    if (visible && question) {
      // reset state for new question
      setSelectedIdx(null);
      setFeedbackType(null);
      setSecondsLeft(AUTO_DISMISS_MS / 1000);
      setRenderPopup(true);

      // slide up
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: SLIDE_DURATION,
        useNativeDriver: true,
      }).start();

      // auto-dismiss countdown
      countdownTimer.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearAllTimers();
            animateOut(() => onDismiss && onDismiss("timeout", question?.id));
            return 0;
          }
          return prev - 1;
        });
      }, COUNTDOWN_INTERVAL);

      // hard auto-dismiss fallback (in case countdown logic drifts)
      autoDismissTimer.current = setTimeout(() => {
        clearAllTimers();
        animateOut(() => onDismiss && onDismiss("timeout", question?.id));
      }, AUTO_DISMISS_MS + 500);
    } else if (!visible && renderPopup) {
      clearAllTimers();
      animateOut(null);
    }

    return () => clearAllTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, question?.id]);

  // --- handlers ---------------------------------------------------------
  const handleOptionPress = useCallback(
    async (idx) => {
      if (selectedIdx !== null) return; // already answered
      setSelectedIdx(idx);
      clearAllTimers();

      const selectedAnswer = `option${idx + 1}`;
      // Show "submitting..." while waiting for API
      setFeedbackType("submitted");

      // Await API response for correctness feedback
      const result = onAnswer ? await onAnswer(question.id, selectedAnswer) : null;

      if (result) {
        setFeedbackType(result.is_correct ? "correct" : "incorrect");
      }

      feedbackTimer.current = setTimeout(() => {
        animateOut(() => onDismiss && onDismiss("answered", question?.id));
      }, FEEDBACK_MS);
    },
    [selectedIdx, question, onAnswer, onDismiss, clearAllTimers, animateOut],
  );

  const handleSkip = useCallback(() => {
    if (selectedIdx !== null) return;
    clearAllTimers();
    if (onSkip) onSkip(question?.id, "button");
    animateOut(null);
  }, [selectedIdx, question, onSkip, clearAllTimers, animateOut]);

  const handleBackdropPress = useCallback(() => {
    if (selectedIdx !== null) return; // let feedback finish
    clearAllTimers();
    if (onSkip) onSkip(question?.id, "backdrop");
    animateOut(null);
  }, [selectedIdx, question, clearAllTimers, onSkip, animateOut]);

  // --- layout (must stay before early return to keep hook order stable) ---
  const isMobile = windowWidth < 600;
  const popupWidth = isMobile ? windowWidth - 32 : MAX_WIDTH;

  // --- styles (hook — must be called unconditionally) ---------------------
  const s = useMemo(() => makeStyles(colors, popupWidth), [colors, popupWidth]);

  // --- nothing to render ------------------------------------------------
  if (!renderPopup || !question) return null;

  const scale = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.9, 1],
  });

  const opacity = slideAnim.interpolate({
    inputRange: [0, 0.4, 1],
    outputRange: [0, 1, 1],
  });

  const countdownProgress = secondsLeft / (AUTO_DISMISS_MS / 1000);

  return (
    <View style={s.overlay} pointerEvents="box-none">
      {/* Subtle backdrop — tapping dismisses */}
      <TouchableOpacity
        style={s.backdrop}
        activeOpacity={1}
        onPress={handleBackdropPress}
        accessible={false}
      />

      {/* Popup card */}
      <Animated.View
        style={[
          s.card,
          {
            transform: [{ scale }],
            opacity,
          },
        ]}
        accessibilityRole="alert"
        accessibilityLabel="Knowledge check question"
      >
        {/* Header row */}
        <View style={s.header}>
          <View style={s.headerLeft}>
            <MaterialCommunityIcons
              name="head-question-outline"
              size={18}
              color={colors.accentPrimary}
            />
            <Text style={s.headerTitle}>Quick Check</Text>
          </View>
        </View>

        {/* Countdown bar */}
        {selectedIdx === null && (
          <View style={s.countdownTrack}>
            <View
              style={[
                s.countdownFill,
                { width: `${countdownProgress * 100}%` },
              ]}
            />
          </View>
        )}

        {/* Question text */}
        <Text style={s.questionText}>{question.question_text}</Text>

        {/* Options */}
        <View style={s.optionsContainer}>
          {options.map((optionText, idx) => {
            const isSelected = selectedIdx === idx;
            const isAnswered = selectedIdx !== null;

            let optionStyle = s.option;
            let labelStyle = s.optionLabel;
            let textStyle = s.optionText;
            let badgeBg = colors.optionBg;

            if (isSelected) {
              optionStyle = [s.option, s.optionSelected];
              labelStyle = [s.optionLabel, s.optionLabelSelected];
              textStyle = [s.optionText, { color: colors.accentPrimary }];
              badgeBg = colors.accentPrimary;
            } else if (isAnswered) {
              optionStyle = [s.option, { opacity: 0.45 }];
            }

            return (
              <TouchableOpacity
                key={idx}
                style={optionStyle}
                onPress={() => handleOptionPress(idx)}
                disabled={isAnswered}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`Option ${OPTION_LABELS[idx]}: ${optionText}`}
                accessibilityState={{ selected: isSelected }}
              >
                <View
                  style={[
                    s.optionBadge,
                    {
                      backgroundColor: isSelected
                        ? colors.accentPrimary
                        : colors.optionBg,
                    },
                  ]}
                >
                  <Text
                    style={[
                      s.optionBadgeText,
                      {
                        color: isSelected ? "#ffffff" : colors.textSecondary,
                      },
                    ]}
                  >
                    {OPTION_LABELS[idx]}
                  </Text>
                </View>
                <Text style={textStyle} numberOfLines={3}>
                  {optionText}
                </Text>
                {isSelected && (
                  <MaterialCommunityIcons
                    name="check-circle"
                    size={18}
                    color={colors.accentPrimary}
                    style={{ marginLeft: "auto" }}
                  />
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Feedback banner (shown after selection) */}
        {feedbackType === "submitted" && (
          <View style={s.feedbackBanner}>
            <MaterialCommunityIcons
              name="send-check"
              size={16}
              color={colors.accentPrimary}
            />
            <Text style={s.feedbackText}>Checking...</Text>
          </View>
        )}
        {feedbackType === "correct" && (
          <View style={[s.feedbackBanner, { backgroundColor: isDarkMode ? "rgba(52,211,153,0.15)" : "#ecfdf5" }]}>
            <MaterialCommunityIcons
              name="check-circle"
              size={16}
              color={isDarkMode ? "#34d399" : "#10b981"}
            />
            <Text style={[s.feedbackText, { color: isDarkMode ? "#34d399" : "#10b981" }]}>Correct!</Text>
          </View>
        )}
        {feedbackType === "incorrect" && (
          <View style={[s.feedbackBanner, { backgroundColor: isDarkMode ? "rgba(248,113,113,0.15)" : "#fef2f2" }]}>
            <MaterialCommunityIcons
              name="close-circle"
              size={16}
              color={isDarkMode ? "#f87171" : "#ef4444"}
            />
            <Text style={[s.feedbackText, { color: isDarkMode ? "#f87171" : "#ef4444" }]}>Not quite!</Text>
          </View>
        )}

        {/* Skip / disable links */}
        {selectedIdx === null && (
          <View style={s.footerActions}>
            <TouchableOpacity
              style={s.skipButton}
              onPress={handleSkip}
              activeOpacity={0.6}
              accessibilityRole="button"
              accessibilityLabel="Skip this question"
            >
              <Text style={s.skipText}>Skip for now</Text>
            </TouchableOpacity>
            {onDisablePopups && (
              <TouchableOpacity
                style={s.disableButton}
                onPress={() => {
                  clearAllTimers();
                  if (onDisablePopups) onDisablePopups();
                  animateOut(null);
                }}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Don't show knowledge checks again"
              >
                <Text style={s.disableText}>Don't show these again</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </Animated.View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles factory (mirrors project's makeStyles pattern)
// ---------------------------------------------------------------------------

function makeStyles(c, popupWidth) {
  return StyleSheet.create({
    overlay: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: "center",
      alignItems: "center",
      padding: 16,
      zIndex: 9000,
      pointerEvents: "box-none",
    },
    backdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.backdrop,
    },
    card: {
      width: popupWidth,
      backgroundColor: c.cardBg,
      borderRadius: 16,
      paddingTop: 16,
      paddingBottom: 12,
      paddingHorizontal: 18,
      // shadow (cross-platform)
      ...Platform.select({
        web: {
          boxShadow: `0 8px 32px rgba(0,0,0,${c.shadowOpacity})`,
        },
        default: {
          shadowColor: c.shadow,
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: c.shadowOpacity,
          shadowRadius: 24,
          elevation: 12,
        },
      }),
      borderWidth: 1.5,
      borderColor: c.accentPrimary,
    },

    // Header
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    headerLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    headerTitle: {
      fontSize: 13,
      fontWeight: "700",
      color: c.accentPrimary,
      letterSpacing: 0.3,
      textTransform: "uppercase",
    },
    difficultyBadge: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 8,
    },
    difficultyText: {
      fontSize: 11,
      fontWeight: "600",
    },

    // Countdown
    countdownTrack: {
      height: 3,
      backgroundColor: c.countdownTrack,
      borderRadius: 2,
      marginBottom: 12,
      overflow: "hidden",
    },
    countdownFill: {
      height: 3,
      backgroundColor: c.countdownFill,
      borderRadius: 2,
    },

    // Question
    questionText: {
      fontSize: 17,
      fontWeight: "500",
      color: c.textPrimary,
      lineHeight: 25,
      marginBottom: 14,
    },

    // Options
    optionsContainer: {
      gap: 8,
    },
    option: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 11,
      paddingHorizontal: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.optionBorder,
      backgroundColor: c.optionBg,
      minHeight: 44, // accessibility tap target
    },
    optionSelected: {
      borderColor: c.accentPrimary,
      backgroundColor: c.optionBgHover,
    },
    optionBadge: {
      width: 26,
      height: 26,
      borderRadius: 7,
      alignItems: "center",
      justifyContent: "center",
    },
    optionBadgeText: {
      fontSize: 12,
      fontWeight: "700",
    },
    optionText: {
      flex: 1,
      fontSize: 15,
      color: c.textPrimary,
      lineHeight: 22,
    },
    optionLabel: {},
    optionLabelSelected: {},

    // Feedback
    feedbackBanner: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      marginTop: 10,
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 8,
      backgroundColor: c.optionBgHover,
    },
    feedbackText: {
      fontSize: 13,
      fontWeight: "600",
      color: c.accentPrimary,
    },

    // Footer actions
    footerActions: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 6,
    },

    // Skip
    skipButton: {
      paddingVertical: 10,
      paddingHorizontal: 16,
      minHeight: 44, // accessibility tap target
      justifyContent: "center",
    },
    skipText: {
      fontSize: 13,
      color: c.skipColor,
      textDecorationLine: "underline",
    },

    // Disable
    disableButton: {
      paddingVertical: 10,
      paddingHorizontal: 16,
      minHeight: 44, // accessibility tap target
      justifyContent: "center",
    },
    disableText: {
      fontSize: 11,
      color: c.textMuted,
    },
  });
}
